export const DEFAULT_DOCUMENT_EXTENSIONS = ['pdf', 'docx', 'txt'];
export const DEFAULT_DOCUMENT_MAX_SIZE_MB = 10;

export type DocumentFileValidationError = 'type' | 'size';

export type DocumentFileValidationResult =
  | {
      isValid: true;
      allowedExtensions: string[];
      maxSizeMB: number;
    }
  | {
      isValid: false;
      error: DocumentFileValidationError;
      allowedExtensions: string[];
      maxSizeMB: number;
    };

const normalizeExtensions = (extensions: string[]): string[] => extensions.map((extension) => extension.trim().replace('.', '').toLowerCase()).filter(Boolean);

export const getExtensionsFromAccept = (accept?: string): string[] => {
  if (!accept) return DEFAULT_DOCUMENT_EXTENSIONS;
  const extensions = accept
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.startsWith('.'))
    .map((item) => item.slice(1));
  const normalized = normalizeExtensions(extensions);
  return normalized.length ? normalized : DEFAULT_DOCUMENT_EXTENSIONS;
};

export const formatAllowedExtensions = (extensions: string[]): string =>
  normalizeExtensions(extensions)
    .map((ext) => ext.toUpperCase())
    .join(', ');

export const validateDocumentFile = (
  file: File,
  options?: {
    allowedExtensions?: string[];
    maxSizeMB?: number;
  }
): DocumentFileValidationResult => {
  const allowedExtensions = normalizeExtensions(options?.allowedExtensions ?? DEFAULT_DOCUMENT_EXTENSIONS);
  const maxSizeMB = options?.maxSizeMB ?? DEFAULT_DOCUMENT_MAX_SIZE_MB;
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';

  if (!allowedExtensions.includes(extension)) {
    return {
      isValid: false,
      error: 'type',
      allowedExtensions,
      maxSizeMB,
    };
  }

  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return {
      isValid: false,
      error: 'size',
      allowedExtensions,
      maxSizeMB,
    };
  }

  return {
    isValid: true,
    allowedExtensions,
    maxSizeMB,
  };
};
